import { IdentityLink } from "@/components/identity-link";
import { ActingPrincipal } from "@/components/ui/ActingPrincipal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/Avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { Text } from "@/components/ui/Text";
import { HumanizeDateTime } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { useSlugs } from "@/contexts/Sdk";
import type { ChallengeBucket } from "@gram/client/models/components/challengebucket.js";
import { useMembers } from "@gram/client/react-query/members.js";
import { Column } from "@/components/ui/Table";
import { KeyRound } from "lucide-react";
import { useMemo } from "react";
import { OutcomeBadge } from "./ChallengesTab";
import { ResourceLink } from "./ResourceLink";
import { useResourceLinkMaps } from "./useResourceLinkMaps";
import {
  getInitials,
  principalDisplayName,
  reasonLabel,
} from "./challengeHelpers";

export function useChallengeRowColumns(
  animatingOutIds?: Set<string>,
  outcomeFilter?: string,
  onToggleBucket?: (bucketId: string) => void,
  expandedChildIds?: Set<string>,
  recentlyResolvedIds?: Set<string>,
  /**
   * Readable agents by id. A challenge row carries only the urn, so without
   * this every agent reads as `agent:<uuid>` — and an id off a challenge is
   * not permission to open the agent, so a row outside this map is named by
   * its id and does not link.
   */
  agentsById?: Map<string, { name: string }>,
): Column<ChallengeBucket>[] {
  const { orgSlug } = useSlugs();
  const { data: membersData } = useMembers();
  const { projectMap, toolsetMap, mcpServerMap } = useResourceLinkMaps();
  const memberMap = useMemo(() => {
    const m = new Map<string, { email: string; photoUrl?: string }>();
    for (const member of membersData?.members ?? []) {
      m.set(member.id, { email: member.email, photoUrl: member.photoUrl });
    }
    return m;
  }, [membersData]);

  return useMemo(() => {
    const isChild = (row: ChallengeBucket) =>
      expandedChildIds?.has(row.id) ?? false;

    const rowFade = (row: ChallengeBucket) =>
      animatingOutIds?.has(row.id)
        ? "opacity-0 transition-opacity duration-1000"
        : outcomeFilter === "deny" &&
          (row.outcome === "allow" ||
            row.resolvedAt ||
            recentlyResolvedIds?.has(row.id)) &&
          "opacity-40 transition-opacity duration-1000";

    return [
      {
        key: "avatar",
        header: "",
        width: "40px",
        render: (row: ChallengeBucket) => {
          if (isChild(row)) return null;
          // An agent is not a person, and the identity column beside this one
          // draws its own square face. A round avatar here would put a second,
          // human-looking mark on the same row.
          if (row.principalType === "agent") return null;
          const isApiKey = row.principalType === "api_key";
          const display = principalDisplayName(row.userEmail, row.principalUrn);
          return (
            <div className={cn(rowFade(row))}>
              <Avatar className="h-8 w-8">
                {row.photoUrl && (
                  <AvatarImage src={row.photoUrl} alt={display} />
                )}
                <AvatarFallback className="text-[11px]">
                  {isApiKey ? (
                    <KeyRound className="h-4 w-4" />
                  ) : (
                    getInitials(display)
                  )}
                </AvatarFallback>
              </Avatar>
            </div>
          );
        },
      },
      {
        key: "identity",
        header: "Identity",
        width: "1.2fr",
        render: (row: ChallengeBucket) => {
          if (isChild(row)) return null;
          // An agent gets the shared renderer: its own square face and a link
          // to its identity page, in place of the raw "agent:<uuid>" under a
          // person's initials. No authorizer — a challenge records the one
          // principal that was checked, so naming a person here would invent a
          // delegation this row knows nothing about.
          if (row.principalType === "agent") {
            const agentId = row.principalUrn.replace(/^agent:/, "");
            const agent = agentsById?.get(agentId);
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn("min-w-0 text-sm font-medium", rowFade(row))}
                  >
                    <ActingPrincipal
                      agent={{ id: agentId, name: agent?.name }}
                      linked={agent !== undefined}
                    />
                  </div>
                </TooltipTrigger>
                {row.roleSlugs.length > 0 && (
                  <TooltipContent side="bottom">
                    Roles: {row.roleSlugs.join(", ")}
                  </TooltipContent>
                )}
              </Tooltip>
            );
          }
          const display = principalDisplayName(row.userEmail, row.principalUrn);
          // Only user principals are people; an api_key principal resolves to
          // no identity page.
          const identifier =
            row.principalType === "user" ? { urn: row.principalUrn } : null;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <Text
                  variant="body"
                  className={cn(
                    "min-w-0 truncate text-sm font-medium",
                    rowFade(row),
                  )}
                >
                  <IdentityLink identifier={identifier}>{display}</IdentityLink>
                </Text>
              </TooltipTrigger>
              {row.roleSlugs.length > 0 && (
                <TooltipContent side="bottom">
                  Roles: {row.roleSlugs.join(", ")}
                </TooltipContent>
              )}
            </Tooltip>
          );
        },
      },
      {
        key: "outcome",
        header: "Outcome",
        width: "80px",
        render: (row: ChallengeBucket) => {
          if (isChild(row)) return null;
          const resolved =
            !!row.resolvedAt || (recentlyResolvedIds?.has(row.id) ?? false);
          return (
            <div className={cn(rowFade(row))}>
              <OutcomeBadge outcome={row.outcome} resolved={resolved} />
            </div>
          );
        },
      },
      {
        key: "scope",
        header: "Required Scope",
        width: "1fr",
        render: (row: ChallengeBucket) => (
          <Tooltip>
            <TooltipTrigger asChild>
              <code
                className={cn(
                  "bg-muted min-w-0 truncate px-1.5 py-0.5 font-mono text-xs",
                  rowFade(row),
                )}
              >
                {row.scope}
              </code>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p className="text-xs">
                {reasonLabel(row.reason)}
                {row.evaluatedGrantCount > 0 &&
                  ` (${row.matchedGrantCount} of ${row.evaluatedGrantCount} grants matched)`}
              </p>
            </TooltipContent>
          </Tooltip>
        ),
      },
      {
        key: "resource",
        header: "Resource",
        width: "1.2fr",
        render: (row: ChallengeBucket) => (
          <div className={cn("min-w-0 truncate", rowFade(row))}>
            <ResourceLink
              challenge={row}
              orgSlug={orgSlug ?? ""}
              projectMap={projectMap}
              toolsetMap={toolsetMap}
              mcpServerMap={mcpServerMap}
            />
          </div>
        ),
      },
      {
        key: "resolvedBy",
        header: "Resolved By",
        width: "100px",
        render: (row: ChallengeBucket) => {
          if (!row.resolvedBy) {
            return (
              <Text variant="body" className="text-muted-foreground/40 text-sm">
                —
              </Text>
            );
          }
          const userId = row.resolvedBy.replace(/^user:/, "");
          const member = memberMap.get(userId);
          const display = member?.email ?? row.resolvedBy;
          return (
            <Tooltip>
              {/* The avatar stays the trigger: asChild hands it the ref the
                  tooltip positions against, which a wrapper would swallow. The
                  identity column beside this one carries the link. */}
              <TooltipTrigger asChild>
                <Avatar className="h-7 w-7">
                  {member?.photoUrl && (
                    <AvatarImage src={member.photoUrl} alt={display} />
                  )}
                  <AvatarFallback className="text-[10px]">
                    {getInitials(display)}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>{display}</TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        key: "timestamp",
        header: "Time",
        width: "1fr",
        render: (row: ChallengeBucket) => {
          const count = row.challengeCount;
          return (
            <div className={cn("flex items-center gap-1.5", rowFade(row))}>
              <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                  <Text
                    variant="body"
                    className="text-muted-foreground cursor-default text-sm whitespace-nowrap underline decoration-dotted underline-offset-4"
                  >
                    <HumanizeDateTime date={row.lastSeen} />
                  </Text>
                </TooltipTrigger>
                <TooltipContent>{row.lastSeen.toLocaleString()}</TooltipContent>
              </Tooltip>
              {count > 1 && onToggleBucket && (
                <button
                  type="button"
                  onClick={() => onToggleBucket(row.id)}
                  className="text-muted-foreground bg-muted hover:bg-primary/10 hover:text-primary cursor-pointer rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums transition-colors"
                >
                  ×{count}
                </button>
              )}
            </div>
          );
        },
      },
    ];
  }, [
    orgSlug,
    projectMap,
    toolsetMap,
    mcpServerMap,
    memberMap,
    agentsById,
    animatingOutIds,
    outcomeFilter,
    onToggleBucket,
    expandedChildIds,
    recentlyResolvedIds,
  ]);
}
